import { useState, type ReactNode } from 'react';

import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { docsAssetUrl, docsImageUrl } from '../../lib/docsAsset';
import { DocsAssetFallback } from './DocsAssetFallback';

interface ScreencastProps {
  /**
   * 두 형태를 받는다:
   *  - **절대 URL** — 대표님이 에디터에서 올린 파일 하나(media-service → CDN).
   *  - **상대 키(확장자 없음)** — CI 가 CDN 에 넣은 `<key>.webm` / `.mp4` / `.webp`.
   */
  src: string;
  /** 화면 낭독기용 설명. 영상이 보여주는 동작을 문장으로. */
  alt: string;
  /** 첫 프레임 이미지. 업로드본은 선택, 상대 키는 `<key>.webp` 를 자동으로 쓴다. */
  poster?: string;
  children?: ReactNode;
}

const isAbsolute = (src: string) => /^https?:\/\//i.test(src);

/**
 * 한 동작을 보여주는 무음 루프 영상. **GIF 를 쓰지 않는 자리다.**
 *
 * 같은 10초 클립이 GIF 로는 5~15MB, webm/mp4 로는 1MB 아래다. 더 중요한 건 색인데,
 * GIF 는 256색이라 적산 화면처럼 숫자·표가 빽빽한 UI 에서 글자가 뭉개져 못 읽는다.
 * 그리고 GIF 는 멈출 수가 없어서, 따라 하려는 사용자가 루프를 계속 노려보게 된다.
 *
 * `preload="metadata"` 라 본편은 재생 전까지 안 받는다. `none` 으로 두면 자동재생이
 * 꺼진 사용자(동작 줄이기)에게는 로드가 시작조차 안 돼 error 도, 자리표시도 안 뜬다.
 */
export function Screencast({ src, alt, poster, children }: ScreencastProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [missing, setMissing] = useState(false);

  const uploaded = isAbsolute(src);
  const posterUrl = poster
    ? docsImageUrl(poster)
    : uploaded
      ? undefined
      : docsImageUrl(`${src}.webp`);

  return (
    <figure className="my-7">
      <div className="overflow-hidden rounded-xl border border-neutral-200 dark:border-white/12">
        {missing ? (
          <DocsAssetFallback assetKey={uploaded ? src : `${src}.mp4`} alt={alt} />
        ) : (
          // 무음 영상이라 자막 트랙이 없다. 설명은 aria-label 과 캡션이 진다.
          <video
            className="block w-full"
            poster={posterUrl}
            preload="metadata"
            controls
            muted
            loop
            playsInline
            autoPlay={!reducedMotion}
            aria-label={alt}
            onError={() => setMissing(true)}
            // 업로드본은 파일이 하나다. 여러 <source> 를 두면 첫 항목이 404 날 때
            // 브라우저가 다음 후보를 찾다가 엉뚱한 시점에 error 를 던진다.
            {...(uploaded ? { src: docsAssetUrl(src) } : {})}
          >
            {!uploaded && (
              <>
                {/* source 의 error 는 버블링하지 않는다 — 각 source 에도 건다. */}
                <source
                  src={docsAssetUrl(`${src}.webm`)}
                  type="video/webm"
                  onError={() => setMissing(true)}
                />
                <source
                  src={docsAssetUrl(`${src}.mp4`)}
                  type="video/mp4"
                  onError={() => setMissing(true)}
                />
              </>
            )}
          </video>
        )}
      </div>
      {children && (
        <figcaption className="mt-2.5 text-[12.5px] leading-6 text-neutral-500 dark:text-neutral-400">
          {children}
        </figcaption>
      )}
    </figure>
  );
}
